import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MdBarChart, MdChevronRight, MdInfoOutline } from "react-icons/md";
import Card from "../../../components/card";
import { getMeusPaineis } from "../../../services/paineisApi";
import { MeusPaineisViewModel } from "../../../interfaces/Paineis";
import SeloFrescor from "./components/SeloFrescor";
import PedirAcessoModal from "./components/PedirAcessoModal";
import { agruparPorArea, destinoDaLista, filtrarRelatorios } from "./logica";

const AVISO =
  "flex items-start gap-3 rounded-2xl border border-dashed border-gray-200 p-10 text-center text-sm text-gray-600 dark:border-white/15 dark:text-gray-300";

/** "Meus painéis" (permissão `bi.view`): relatórios liberados, por área. Um só relatório abre direto. */
const PaineisPage: React.FC = () => {
  const navigate = useNavigate();
  // `?lista=1` (link "Meus painéis" do visualizador): mostra a lista mesmo com um só relatório.
  const forcarLista = useSearchParams()[0].get("lista") === "1";
  const [dados, setDados] = useState<MeusPaineisViewModel | null>(null);
  const [erro, setErro] = useState(false);
  const [busca, setBusca] = useState("");
  const [pedindoAcesso, setPedindoAcesso] = useState(false);

  useEffect(() => {
    let vivo = true;
    getMeusPaineis()
      .then((d) => {
        if (!vivo) return;
        const destino = destinoDaLista(d, forcarLista);
        if (destino.tipo === "navegar") {
          navigate(`/admin/paineis/${destino.id}`, { replace: true });
          return;
        }
        setDados(d);
      })
      .catch(() => vivo && setErro(true));
    return () => {
      vivo = false;
    };
  }, [navigate, forcarLista]);

  const grupos = useMemo(
    () => (dados ? agruparPorArea(filtrarRelatorios(dados.relatorios, busca)) : []),
    [dados, busca]
  );
  const semResultado = !!dados && dados.relatorios.length > 1 && grupos.length === 0;

  const destino = dados ? destinoDaLista(dados, forcarLista) : null;
  const mensagem = destino?.tipo === "mensagem" ? destino.texto : null;

  return (
    <div className="mt-5 animate-fade-up">
      <header className="mb-6 flex items-center gap-4">
        <span
          className="dv-aura flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow"
          aria-hidden="true"
        >
          <MdBarChart className="h-6 w-6" />
        </span>
        <div className="min-w-0">
          <span className="inline-flex items-center rounded-full bg-dv-blue-500/10 px-3 py-1 font-display text-xs font-bold uppercase tracking-[0.18em] text-dv-blue-600 dark:text-dv-cyan-300">
            Power BI
          </span>
          <h1 className="mt-1.5 text-2xl font-extrabold tracking-tight text-navy-700 dark:text-white">
            Meus painéis
          </h1>
        </div>
      </header>

      {dados && (
        <div className="mb-5">
          <button
            type="button"
            onClick={() => setPedindoAcesso(true)}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-navy-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 dark:border-white/15 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700 dark:focus-visible:ring-dv-cyan-300"
          >
            Pedir acesso a um painel
          </button>
        </div>
      )}

      {erro && (
        <div
          role="alert"
          className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300"
        >
          Não foi possível carregar seus painéis. Atualize a página; se continuar, avise o administrador.
        </div>
      )}

      {!erro && !dados && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true" aria-label="Carregando painéis">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-[20px] bg-gray-100 dark:bg-navy-800" />
          ))}
        </div>
      )}

      {mensagem && (
        <div className={AVISO} role="status">
          <MdInfoOutline className="mt-0.5 h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
          <p className="text-left">{mensagem}</p>
        </div>
      )}

      {dados && dados.relatorios.length > 1 && (
        <div className="mb-5">
          <label htmlFor="busca-paineis" className="sr-only">
            Buscar painel
          </label>
          <input
            id="busca-paineis"
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome ou área"
            className="w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-navy-700 outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 dark:border-white/15 dark:bg-navy-800 dark:text-white dark:focus-visible:ring-dv-cyan-300"
          />
        </div>
      )}

      {semResultado && (
        <div className={AVISO} role="status">
          <MdInfoOutline className="mt-0.5 h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
          <p className="text-left">Nenhum painel encontrado para "{busca}". Tente outra palavra.</p>
        </div>
      )}

      {grupos.map(([area, itens]) => (
        <section key={area} className="mb-6" aria-label={`Área ${area}`}>
          <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">{area}</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {itens.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => navigate(`/admin/paineis/${r.id}`)}
                className="text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 rounded-[20px] dark:focus-visible:ring-dv-cyan-300"
              >
                <Card extra="h-full p-5 transition-all duration-200 hover:-translate-y-0.5 active:scale-[.98]">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-display text-base font-bold text-navy-700 dark:text-white">{r.nome}</p>
                    <MdChevronRight className="h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                  </div>
                  <div className="mt-3">
                    <SeloFrescor frescor={r.frescor} ultimoRefreshEm={r.ultimoRefreshEm} />
                  </div>
                </Card>
              </button>
            ))}
          </div>
        </section>
      ))}
      {pedindoAcesso && <PedirAcessoModal onClose={() => setPedindoAcesso(false)} />}
    </div>
  );
};

export default PaineisPage;
